import { useEffect, useState } from "react";
import {
  BadgeIndianRupee, BarChart3, Briefcase, Building2, CalendarClock, FileText, GraduationCap,
  Headphones, HeartPulse, Landmark, Package, PackageCheck, Rocket, Scale, Search, ShoppingBag,
  Store, Truck, UserCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FinalCta, QuoteTrackButtons, SectionHead, StepsLine, usePageMeta } from "../components/service/ServiceBits";
import api from "../lib/api";
import VideoHeroBg from "../components/service/VideoHeroBg";

const INDUSTRIES = [
  { icon: Building2, label: "Corporate" }, { icon: Briefcase, label: "SME" },
  { icon: Store, label: "Retail" }, { icon: Package, label: "Manufacturing" },
  { icon: Rocket, label: "Startup" }, { icon: GraduationCap, label: "Education" },
  { icon: HeartPulse, label: "Healthcare" }, { icon: Scale, label: "Legal" },
  { icon: Landmark, label: "Financial Services" }, { icon: ShoppingBag, label: "eCommerce" },
];

const SERVICES = [
  { icon: FileText, title: "Corporate Documents", desc: "Contracts, legal papers and office documents moved securely.",
    img: "https://images.unsplash.com/photo-1449247666642-264389f5f5b1?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Package, title: "Business Parcels", desc: "Samples, products and supplies shipped reliably.",
    img: "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: CalendarClock, title: "Regular Pickup", desc: "Scheduled pickups that match your business routine.",
    img: "https://images.pexels.com/photos/12725455/pexels-photo-12725455.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { icon: PackageCheck, title: "Bulk Shipments", desc: "High-volume shipping handled with ease.",
    img: "https://images.unsplash.com/photo-1549194388-f61be84a6e9e?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Truck, title: "Intercity Courier", desc: "Fast movement between cities across India.",
    img: "https://images.unsplash.com/photo-1771429631978-674f8689aca6?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
  { icon: Building2, title: "International Business Shipping", desc: "Exports and global business shipments with documentation support.",
    img: "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&w=800&q=80" },
];

const BENEFITS = [
  { icon: UserCheck, title: "Dedicated Account Manager", desc: "One point of contact who knows your business." },
  { icon: BadgeIndianRupee, title: "Business Pricing", desc: "Pricing structures designed for regular shippers." },
  { icon: CalendarClock, title: "Regular Pickup", desc: "Recurring collections from your office or warehouse." },
  { icon: Search, title: "Shipment Tracking", desc: "Visibility on every shipment, every step." },
  { icon: Truck, title: "Multiple Courier Partners", desc: "The right courier for each route and requirement." },
  { icon: Headphones, title: "Operational Support", desc: "Assistance for day-to-day shipping operations." },
];

const PROCESS = [
  { n: "01", title: "Request" }, { n: "02", title: "Pickup" }, { n: "03", title: "Processing" },
  { n: "04", title: "Transit" }, { n: "05", title: "Delivery" }, { n: "06", title: "Reporting" },
];

const DASH = [
  { l: "Total Shipments", v: "3,482" }, { l: "Delivered", v: "3,201" },
  { l: "In Transit", v: "214" }, { l: "Pending", v: "67" },
];

export default function BusinessCourier() {
  const [contact, setContact] = useState(null);
  usePageMeta(
    "Business Courier Solutions for Companies | IndiaFast",
    "Professional courier and shipping support for companies, offices, retailers and growing businesses — dedicated account manager, regular pickup, business pricing and tracking.",
    "/business-courier"
  );
  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="business-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-24 pt-32 sm:pt-36" data-testid="business-hero">
        <VideoHeroBg mp4="/videos/sea.mp4" webm="/videos/sea.webm"
                     poster="https://static.prod-images.emergentagent.com/jobs/6e217b82-4821-4792-98ef-42642f657e6a/images/f25b9942544222a33a50c79e7ee873408c9d5e2723058eb519fa1bdc859edf4e.jpeg"
                     posterAlt="IndiaFast branded container ship at sea" badge="Ocean Freight" chip="AWB IFAST2000001 · Vessel departed Nhava Sheva" testid="business-hero-video" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-25" aria-hidden="true">
          <line x1="15%" y1="70%" x2="45%" y2="35%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="45%" y1="35%" x2="80%" y2="60%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <circle cx="15%" cy="70%" r="4" fill="#f97316" className="network-pulse" />
          <circle cx="45%" cy="35%" r="4" fill="#ffffff" className="network-pulse" style={{ animationDelay: "0.8s" }} />
          <circle cx="80%" cy="60%" r="4" fill="#16a34a" className="network-pulse" style={{ animationDelay: "1.5s" }} />
        </svg>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up text-xs font-bold uppercase tracking-[0.25em] text-saffron">Business Courier</p>
          <h1 className="animate-fade-up stagger-1 mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            Courier Solutions for <span className="text-saffron">Modern Businesses</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Professional courier and shipping support for companies, offices, retailers and growing businesses.
          </p>
          <div className="animate-fade-up stagger-3"><QuoteTrackButtons quoteLabel="Get Business Quote" testid="business" /></div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-white py-16 sm:py-20" data-testid="business-industries">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Who We Serve" title="Trusted Across Industries" center />
          <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {INDUSTRIES.map((a, i) => (
              <div key={i} data-testid={`business-industry-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-lg">
                <span className="mx-auto grid h-11 w-11 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><a.icon className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-bold text-navy">{a.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS SERVICES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="business-services">
        <SectionHead kicker="Business Services" title="Shipping Services Built Around Your Business" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <div key={i} data-testid={`business-service-${i}`}
                 className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-saffron/40 hover:shadow-xl">
              <div className="relative h-36 overflow-hidden">
                <img src={s.img} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-4 top-4 grid h-10 w-10 place-items-center rounded-xl bg-navy/90 text-saffron backdrop-blur"><s.icon className="h-5 w-5" /></span>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-bold text-navy">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-white py-16 sm:py-20 lg:py-24" data-testid="business-benefits">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Business Benefits" title="More Than Deliveries — A Shipping Partnership" />
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <div key={i} data-testid={`business-benefit-${i}`}
                   className="group rounded-2xl border border-slate-200 bg-slate-50 p-6 transition-all hover:-translate-y-1 hover:border-saffron/40 hover:bg-white hover:shadow-xl">
                <span className="grid h-12 w-12 place-items-center rounded-xl bg-navy text-saffron transition-colors group-hover:bg-saffron group-hover:text-white"><b.icon className="h-6 w-6" /></span>
                <h3 className="mt-4 font-heading text-base font-bold text-navy">{b.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CORPORATE DASHBOARD MOCKUP */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20" data-testid="business-dashboard">
        <SectionHead kicker="Corporate Dashboard" title="Your Entire Shipping Operation, One Screen" center />
        <div className="mt-12 rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl sm:p-8" data-testid="business-dashboard-mock">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
            <span className="ml-3 text-[11px] font-semibold text-slate-400">IndiaFast Business Console</span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {DASH.map((s, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`business-dash-${i}`}>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{s.l}</p>
                <p className="mt-1 font-heading text-2xl font-extrabold text-navy">{s.v}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Monthly Spend</p>
              <p className="mt-2 font-heading text-2xl font-extrabold text-navy">₹4.2L</p>
              <p className="text-[11px] font-semibold text-emerald-600">8% under last quarter</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Delivery Performance</p>
              <div className="mt-3 space-y-2.5">
                {[["On-time delivery", 92], ["First-attempt success", 87]].map(([l, w]) => (
                  <div key={l}>
                    <p className="flex justify-between text-[10px] font-semibold text-slate-500"><span>{l}</span><span>{w}%</span></p>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-200"><span className="block h-full rounded-full bg-emerald-500" style={{ width: `${w}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Courier Performance</p>
              <div className="mt-3 space-y-2.5">
                {[["Express air", 95], ["Surface", 88], ["Economy", 84]].map(([l, w]) => (
                  <div key={l}>
                    <p className="flex justify-between text-[10px] font-semibold text-slate-500"><span>{l}</span><span>{w}%</span></p>
                    <div className="mt-1 h-1.5 rounded-full bg-slate-200"><span className="block h-full rounded-full bg-saffron" style={{ width: `${w}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-16 sm:py-20" data-testid="business-process">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <SectionHead kicker="Business Shipping Process" title="Simple for Your Team. Managed by Ours." center />
          <StepsLine steps={PROCESS} testid="business-steps" cols="lg:grid-cols-6" />
        </div>
      </section>

      {/* ACCOUNT MANAGER */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:py-24" data-testid="business-account">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="relative overflow-hidden rounded-3xl">
            <img src="https://images.pexels.com/photos/13062478/pexels-photo-13062478.jpeg?auto=compress&cs=tinysrgb&w=1200"
                 alt="IndiaFast account manager supporting a business customer" loading="lazy" className="h-72 w-full object-cover sm:h-[400px]" />
            <span className="tricolor-beam absolute inset-x-0 bottom-0 h-1.5" />
          </div>
          <div>
            <SectionHead kicker="Dedicated Support" title="A Shipping Partner, Not Just a Courier"
                         sub="Get dedicated support for your regular business shipping requirements — from pricing to pickups to delivery follow-ups." />
            <Link to="/partner-with-us" data-testid="business-account-cta"
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-express">
              Request Business Account <UserCheck className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <FinalCta testid="business-final-cta" title="Let's Move Your Business Forward"
                primaryLabel="Get Business Quote" primaryTo="/rate-calculator" primaryTestid="business-final-quote"
                secondary={<Link to="/support" data-testid="business-final-talk" className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"><Headphones className="h-4 w-4" /> Talk to Our Team</Link>} />
      <Footer contact={contact} />
    </div>
  );
}
